import React from "react";
import {
    Draggable,
    DraggableProvided,
    Droppable,
    DroppableProvided,
    DroppableStateSnapshot,
} from "react-beautiful-dnd";
import styled from "styled-components";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";

import { FlowStepEditorRegistration, FlowStepEditorRegistrationData } from "../../Utilities/RegisterFlowEditor";
import { FlowConstants } from "../../../rule-engine-core/Flow/Utilities/FlowConstants";
import { FlowEditContext } from "../../FlowEditContext";
import { FlowConfiguration } from "../../../rule-engine-core/Flow/FlowConfiguration";
import { EditStackIcon } from "../../../CommonUI/Icon/EditStackIcon";
import { ToolsPicker } from "./ToolsPicker";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { DeleteGrayIcon } from "../../../CommonUI/Icon/DeleteGrayIcon";
import { CloseGrayIcon } from "../../../CommonUI/Icon/CloseGrayIcon";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { OpenIcon } from "../../../CommonUI/Icon/OpenIcon";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { UIButton } from "../../../CommonUI/BuildlingBlocks/UIButton";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

interface IToolBox {
    editContext: FlowEditContext;
    flow: FlowConfiguration;
}

interface IToolBoxState {
    openPicker: boolean;
    editMode: boolean;
    removedSteps: string[];
}

export class ToolBox extends React.Component<IToolBox, IToolBoxState> {
    constructor(props: IToolBox) {
        super(props);

        this.state = {
            openPicker: false,
            removedSteps: [],
            editMode: false,
        };
    }

    onOpenPicker = () => {
        this.setState({
            openPicker: true,
        });
    };

    onClosePicker = () => {
        this.setState({
            openPicker: false,
        });
    };

    onDeleteSteps = () => {
        if (this.state.editMode && this.props.flow.visibleSteps.length > 0 && this.state.removedSteps.length > 0) {
            this.props.editContext.notification = {
                type: "confirm",
                message: "Remove chosen tools? Removed tools you'll be able to found on all tools.",
                actions: [
                    {
                        items: [
                            {
                                renderUI: () => {
                                    return (
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                this.props.editContext.notification = undefined;
                                            }}
                                            data-testid="btn-delete-confirm-no"
                                        >
                                            No
                                        </Button>
                                    );
                                },
                            },
                            {
                                renderUI: () => {
                                    return (
                                        <Button
                                            variant="contained"
                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                            onClick={() => {
                                                this.props.flow.visibleSteps = this.props.flow.visibleSteps.filter(
                                                    (stepName: string) => !this.state.removedSteps.includes(stepName),
                                                );
                                                this.props.flow.favoriteSteps = this.props.flow.favoriteSteps.filter(
                                                    (stepName: string) => !this.state.removedSteps.includes(stepName),
                                                );
                                                this.setState({
                                                    removedSteps: [],
                                                    editMode: this.props.flow.visibleSteps.length > 0,
                                                });
                                                StateManager.propertyChanged(
                                                    this.props.flow,
                                                    FlowConfiguration.VISIBLE_STEPS,
                                                );
                                                this.props.editContext.notification = undefined;
                                            }}
                                            data-testid="btn-delete-confirm-yes"
                                        >
                                            Yes
                                        </Button>
                                    );
                                },
                            },
                        ],
                    },
                ],
            };
        }
    };

    render() {
        const itemsByCategories = FlowStepEditorRegistration.getItemsByCategories();

        return (
            <>
                <Droppable
                    isDropDisabled={true}
                    droppableId={FlowConstants.ToolboxId}
                >
                    {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
                        return (
                            <Box
                                display={"flex"}
                                flexDirection={"column"}
                                border={"1px solid " + UIStyleConstants.GRAY_LIGHTER_COLOR}
                                borderRadius="0px"
                                height={"100%"}
                                width={"300px"}
                                overflow={"auto"}
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                <Box
                                    borderTop={"1px solid " + UIStyleConstants.GRAY_LIGHTER_COLOR}
                                    display={"flex"}
                                    paddingLeft={"10px"}
                                    paddingRight={"10px"}
                                    paddingTop={"10px"}
                                    flexDirection={"column"}
                                >
                                    <Box
                                        display={"flex"}
                                        justifyContent={"start"}
                                    >
                                        <UIButton onClick={() => this.props.editContext.setSelection(this.props.flow)}>
                                            Edit Flow Settings
                                        </UIButton>
                                    </Box>
                                    <ToolboxTitleDiv>
                                        <Box paddingTop={"10px"}>
                                            <Typography variant="subtitle1">Toolbox</Typography>
                                            <Typography variant="caption">
                                                (
                                                {Object.values(itemsByCategories).reduce((s: number, items) => {
                                                    s += items.length;
                                                    return s;
                                                }, 0)}{" "}
                                                steps)
                                            </Typography>
                                        </Box>
                                        <div>
                                            {this.props.flow.visibleSteps.length > 0 && !this.state.editMode && (
                                                <StyledIconButton
                                                    style={{ marginRight: 5 }}
                                                    onClick={() => this.setState({ editMode: true })}
                                                    data-testid="btn-edit-tools"
                                                >
                                                    <EditStackIcon />
                                                </StyledIconButton>
                                            )}
                                            {this.state.editMode && (
                                                <>
                                                    <StyledIconButton
                                                        style={{ marginRight: 5 }}
                                                        onClick={this.onDeleteSteps}
                                                        disabled={this.state.removedSteps.length === 0}
                                                        data-testid="btn-delete-tools"
                                                    >
                                                        <DeleteGrayIcon />
                                                    </StyledIconButton>
                                                    <StyledIconButton
                                                        style={{ marginRight: 5 }}
                                                        onClick={() => this.setState({ editMode: false })}
                                                        data-testid="btn-close-edit-mode"
                                                    >
                                                        <CloseGrayIcon />
                                                    </StyledIconButton>
                                                </>
                                            )}
                                            <Button
                                                onClick={this.onOpenPicker}
                                                data-testid="btn-all-tools"
                                            >
                                                Add tools
                                            </Button>
                                        </div>
                                    </ToolboxTitleDiv>

                                    <ObserveState
                                        listenTo={this.props.flow}
                                        properties={[FlowConfiguration.VISIBLE_STEPS, FlowConfiguration.FAVORITE_STEPS]}
                                        control={() => {
                                            const filteredItems = Object.entries(itemsByCategories).reduce(
                                                (
                                                    obj: { [key: string]: FlowStepEditorRegistrationData[] },
                                                    [category, items],
                                                ) => {
                                                    obj[category] = items.filter((item) =>
                                                        this.props.flow.visibleSteps.includes(item.name),
                                                    );
                                                    return obj;
                                                },
                                                {},
                                            );
                                            return (
                                                <>
                                                    {Object.values(filteredItems).find((items) => items.length > 0) ? (
                                                        <Box
                                                            display={"flex"}
                                                            flexDirection={"column"}
                                                            marginRight={"-10px"}
                                                            paddingRight={"10px"}
                                                        >
                                                            {Object.entries(filteredItems).map(
                                                                ([category, controls]) => {
                                                                    if (controls.length === 0) return null;
                                                                    const doms = controls
                                                                        .map((control, index: number) => {
                                                                            return (
                                                                                <RenderToolboxItem
                                                                                    key={"toolbox" + control.name}
                                                                                    control={control}
                                                                                    snapshot={snapshot}
                                                                                    index={index}
                                                                                    editContext={this.props.editContext}
                                                                                />
                                                                            );
                                                                        })
                                                                        .filter((d) => !!d);

                                                                    if (doms.length === 0) return null;

                                                                    // todo: make this a method on control registration

                                                                    return (
                                                                        <Accordion
                                                                            sx={{
                                                                                border: "none",
                                                                                boxShadow: "none",
                                                                                margin: "0px !important",
                                                                            }}
                                                                            defaultExpanded
                                                                            key={category}
                                                                        >
                                                                            <AccordionSummary
                                                                                sx={{
                                                                                    padding: "0px",
                                                                                    minHeight: "0 !important",
                                                                                    fontStyle: "normal",
                                                                                    fontWeight: 500,
                                                                                    fontSize: "12px",
                                                                                    lineHeight: "15px",
                                                                                    color: UIStyleConstants.GRAY_DARK_COLOR,
                                                                                }}
                                                                                expandIcon={<OpenIcon />}
                                                                            >
                                                                                {category}
                                                                            </AccordionSummary>
                                                                            <AccordionDetails
                                                                                sx={{
                                                                                    display: "flex",
                                                                                    flexDirection: "column",
                                                                                    padding: "0px",
                                                                                }}
                                                                            >
                                                                                {doms}
                                                                            </AccordionDetails>
                                                                        </Accordion>
                                                                    );
                                                                },
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <StyledWrapper>
                                                            <Typography>You have no tools in the toolbox.</Typography>
                                                            <Button
                                                                fullWidth
                                                                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                                variant="contained"
                                                                onClick={this.onOpenPicker}
                                                                data-testid="btn-add-tools"
                                                            >
                                                                Add tools
                                                            </Button>
                                                        </StyledWrapper>
                                                    )}
                                                </>
                                            );
                                        }}
                                    />
                                </Box>
                                {provided.placeholder}
                            </Box>
                        );
                    }}
                </Droppable>
                <ToolsPicker
                    open={this.state.openPicker}
                    editContext={this.props.editContext}
                    flow={this.props.flow}
                    onClose={this.onClosePicker}
                />
            </>
        );
    }
}

function RenderToolboxItem(props: {
    control: FlowStepEditorRegistrationData;
    snapshot: DroppableStateSnapshot;
    index: number;
    editContext: FlowEditContext;
}) {
    const data = props.control;
    const name = props.control.type;

    // this code does not work.  it is the code to leave an item in the toolbox when dragging
    const shouldRenderClone = name === props.snapshot.draggingFromThisWith;

    const isDragging = props.editContext.draggingControl?.source.current === name;
    const ref: React.MutableRefObject<HTMLDivElement | null> = React.useRef<HTMLDivElement>(null);

    return (
        <React.Fragment key={name}>
            {shouldRenderClone && (
                <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    height={"40px"}
                    border={"1px"}
                    borderRadius="4px"
                    padding={"5px"}
                    marginBottom={"10px"}
                    style={{
                        borderStyle: "solid",
                        borderColor: UIStyleConstants.GRAY_LIGHTER_COLOR,
                        background: UIStyleConstants.WHITE_COLOR,
                    }}
                >
                    {data.icon}
                    <UILabel
                        paddingLeft={"10px"}
                        variant={"small"}
                    >
                        {data.name}
                    </UILabel>
                </Box>
            )}
            {
                <Draggable
                    draggableId={props.control.type}
                    index={props.index}
                >
                    {(provided: DraggableProvided) => (
                        <Box
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={(node: HTMLDivElement) => {
                                provided.innerRef(node);
                                ref.current = node;
                            }}
                        >
                            <Tooltip
                                key={"item:" + data.name}
                                title={data.name}
                                placement="top"
                                arrow
                            >
                                <Box
                                    display={"flex"}
                                    alignItems={"center"}
                                    height={"40px"}
                                    border={"1px"}
                                    borderRadius="4px"
                                    padding={"5px"}
                                    marginBottom={"10px"}
                                    style={{
                                        borderStyle: "solid",
                                        borderColor: UIStyleConstants.GRAY_LIGHTER_COLOR,
                                        background: isDragging
                                            ? UIStyleConstants.BLUE_VERY_LIGHT_COLOR
                                            : UIStyleConstants.WHITE_COLOR,
                                        width: isDragging ? "175px" : "100%",
                                    }}
                                >
                                    {data.icon}
                                    <UILabel
                                        paddingLeft={"10px"}
                                        variant={"small"}
                                    >
                                        {isDragging
                                            ? data.name.slice(0, FlowConstants.MAX_CHARS_DRAGGING_STEP)
                                            : data.name}
                                    </UILabel>
                                </Box>
                            </Tooltip>
                        </Box>
                    )}
                </Draggable>
            }
        </React.Fragment>
    );
}

const StyledIconButton = styled(IconButton)`
    &.MuiIconButton-root {
        padding: 0;
    }
`;

const ToolboxTitleDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  margin-top: 5px;

  .MuiTypography-subtitle1 {
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
  }

  .MuiTypography-caption {
    font-style: normal;
    font-weight: 500;
    font-size: 10px;
    line-height: 140%;
    color ${UIStyleConstants.GRAY_COLOR};;
    margin-left: 4px;
  }

  .MuiButton-text {
    padding: 0;

    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 140%;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    text-transform: none;
    min-width: 0;

    &:hover {
      color: ${UIStyleConstants.BLUE_COLOR};
      background-color: transparent;
    }
  }

  > div {
    display: flex;
    align-items: center;
  }
`;

const StyledWrapper = styled.div`
  .MuiTypography-body1 {
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 140%;
    color ${UIStyleConstants.GRAY_COLOR};;
    margin-bottom: 12px;
  }

  .MuiButton-root {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 140%;
    text-transform: none;
  }

  .MuiButton-containedPrimary {
    background-color: ${UIStyleConstants.BLUE_COLOR};
  }
`;
